// ForumThreadsView.js
// Super Admin panel: import DarkForums/BreachForums parser output (CSV file or pasted TSV)
// and browse stored threads. Mirrors RansomwareView conventions.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  Upload, FileText, RefreshCw, CheckCircle, AlertCircle, Database,
  Search, ExternalLink, ClipboardPaste
} from 'lucide-react';

// apiFetch scoped to /api/forum-threads — takes getToken fn, retries once on TOKEN_EXPIRED.
const apiFetch = async (path, getToken, options = {}) => {
  const doFetch = async (tok) => fetch(`/api/forum-threads${path}`, {
    ...options,
    headers: { 'Authorization': `Bearer ${tok}`, 'Accept': 'application/json', ...options.headers }
  });
  let tok = await getToken();
  let res = await doFetch(tok);
  if (res.status === 401) {
    const body = await res.clone().json().catch(() => ({}));
    if (body.code === 'TOKEN_EXPIRED') {
      tok = await getToken({ skipCache: true });
      res = await doFetch(tok);
    }
  }
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.error || `HTTP ${res.status}`);
  }
  return res.json();
};

const fmtDate = (x) => (x ? new Date(x).toLocaleDateString() : '—');

const SOURCES = [
  { id: '', label: 'All sources' },
  { id: 'darkforums', label: 'DarkForums' },
  { id: 'breachforums', label: 'BreachForums' },
];

export default function ForumThreadsView() {
  const { getToken } = useAuth();

  const [file, setFile] = useState(null);
  const [pasted, setPasted] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [source, setSource] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (source) params.set('source', source);
      if (q.trim()) params.set('q', q.trim());
      params.set('limit', '200');
      const [list, s] = await Promise.all([
        apiFetch(`?${params.toString()}`, getToken),
        apiFetch('/stats', getToken).catch(() => null),
      ]);
      setRows(list);
      setStats(s);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, source, q]);

  useEffect(() => { loadList(); }, [loadList]);

  const runImport = async () => {
    setError('');
    setResult(null);
    setImporting(true);
    try {
      let out;
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        out = await apiFetch('/import', getToken, { method: 'POST', body: fd });
      } else if (pasted.trim()) {
        // auto-detect tab vs comma from the header line
        const firstLine = pasted.split('\n')[0] || '';
        const key = firstLine.includes('\t') ? 'tsv' : 'csv';
        out = await apiFetch('/import', getToken, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ [key]: pasted }),
        });
      } else {
        setError('Choose a CSV file or paste TSV data first.');
        setImporting(false);
        return;
      }
      setResult(out);
      setFile(null);
      setPasted('');
      if (fileRef.current) fileRef.current.value = '';
      loadList();
    } catch (e) {
      setError(e.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-slate-900 text-slate-200">
      <div className="px-6 py-4 border-b border-slate-800 flex items-center gap-3">
        <Database className="w-5 h-5 text-cyan-400" />
        <h1 className="text-lg font-semibold text-slate-100">Forum Threads</h1>
        {stats && (
          <span className="ml-auto text-sm text-slate-400">
            {stats.total.toLocaleString()} stored
            {stats.bySource?.map((s) => (
              <span key={s.source} className="ml-3 text-slate-500">
                {s.source}: <span className="text-slate-300">{s.count.toLocaleString()}</span>
              </span>
            ))}
          </span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        {/* Import card */}
        <section className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
          <h2 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
            <Upload className="w-4 h-4 text-cyan-400" /> Import parser output
          </h2>
          <p className="text-xs text-slate-400 mb-4">
            Drop a CSV exported by the forum parser extension, or paste the TSV from its “Copy TSV” button.
            Re-importing is safe — rows upsert on (source, thread_id).
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">CSV file</label>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.tsv"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setPasted(''); }}
                className="block w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-3
                           file:rounded-md file:border-0 file:bg-cyan-600 file:text-white
                           file:cursor-pointer hover:file:bg-cyan-500"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1 flex items-center gap-1">
                <ClipboardPaste className="w-3 h-3" /> or paste TSV / CSV
              </label>
              <textarea
                value={pasted}
                onChange={(e) => { setPasted(e.target.value); setFile(null); }}
                rows={3}
                placeholder="source	thread_id	prefix	title	url	..."
                className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2
                           text-xs font-mono text-slate-200 focus:outline-none focus:border-cyan-500"
              />
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={runImport}
              disabled={importing || (!file && !pasted.trim())}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-cyan-600
                         hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm"
            >
              {importing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {importing ? 'Importing…' : 'Import'}
            </button>
            {file && <span className="text-xs text-slate-400">{file.name}</span>}
          </div>

          {result && (
            <div className="mt-4 flex flex-wrap gap-4 text-sm bg-slate-900/60 border border-slate-700 rounded-md p-3">
              <span className="inline-flex items-center gap-1 text-emerald-400">
                <CheckCircle className="w-4 h-4" /> {result.inserted} new
              </span>
              <span className="text-cyan-300">{result.updated} updated</span>
              <span className="text-slate-400">{result.skipped} skipped</span>
              <span className="text-slate-500">{result.total} total rows</span>
            </div>
          )}
          {error && (
            <div className="mt-4 flex items-center gap-2 text-sm text-red-400 bg-red-950/30 border border-red-900/50 rounded-md p-3">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
        </section>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-md px-3 py-1.5">
            <Search className="w-4 h-4 text-slate-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadList()}
              placeholder="Search titles…"
              className="bg-transparent text-sm text-slate-200 focus:outline-none w-56"
            />
          </div>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-md px-3 py-1.5 text-sm text-slate-200"
          >
            {SOURCES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <button
            onClick={loadList}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-700 hover:bg-slate-800 text-sm text-slate-300"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Table */}
        <div className="border border-slate-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/70 text-slate-400">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Prefix</th>
                <th className="text-left px-4 py-2 font-medium">Title</th>
                <th className="text-left px-4 py-2 font-medium">Source</th>
                <th className="text-left px-4 py-2 font-medium">Author</th>
                <th className="text-left px-4 py-2 font-medium">Created</th>
                <th className="text-right px-4 py-2 font-medium">R</th>
                <th className="text-right px-4 py-2 font-medium">V</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-800 hover:bg-slate-800/40">
                  <td className="px-4 py-2">
                    {r.prefix && (
                      <span className="text-xs px-2 py-0.5 rounded bg-indigo-900/60 text-indigo-200">{r.prefix}</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <a href={r.url} target="_blank" rel="noreferrer"
                       className="text-cyan-400 hover:underline inline-flex items-center gap-1">
                      {r.title}
                      <ExternalLink className="w-3 h-3 opacity-60" />
                    </a>
                  </td>
                  <td className="px-4 py-2 text-slate-400">{r.source}</td>
                  <td className="px-4 py-2 text-slate-300">{r.author || '—'}</td>
                  <td className="px-4 py-2 text-slate-400">{fmtDate(r.created_date)}</td>
                  <td className="px-4 py-2 text-right text-slate-400">{r.replies}</td>
                  <td className="px-4 py-2 text-right text-slate-400">{r.views}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No threads yet — import a CSV above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
